import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./api";
import type { ChallengeKind } from "./challengesApi";

export type DuelStatus = "open" | "active" | "completed" | "cancelled";

export type DuelParticipant = {
  athlete_id: number;
  name: string;
  improvement_pct: number | null;
  current_value: number | null;
  rank: number | null;
};

export type Duel = {
  challenge_id: number;
  slug: string;
  title: string;
  kind: ChallengeKind;
  status: DuelStatus;
  period_start: string;
  period_end: string;
  created_by_athlete_id: number | null;
  created_by_name: string | null;
  participants: DuelParticipant[];
  days_left: number;
  is_creator: boolean;
  is_participant: boolean;
  /** ТД-122 — уровневая вилка создателя открытой дуэли (напр. «44–48»). */
  creator_vdot_band?: string | null;
};

export type GuardOut = {
  has_blockers: boolean;
  warnings: string[];
  payload: Record<string, unknown>;
};

export type CreateDuelOut = {
  challenge_id: number;
  status: string;
  blocked: boolean;
  warnings: string[];
};

export type CreateDuelIn = {
  kind: ChallengeKind;
  period_days?: number;
  invited_athlete_id?: number | null;
  title?: string;
  description?: string;
  scoring_rule?: Record<string, unknown>;
  bypass_guard?: boolean;
};

export function useOpenDuels() {
  return useQuery<Duel[]>({
    queryKey: ["athlete-duels", "open"],
    queryFn: async () => (await api.get<Duel[]>("/athlete/duels/open")).data,
    staleTime: 30_000,
  });
}

export function useMyDuels() {
  return useQuery<Duel[]>({
    queryKey: ["athlete-duels", "my"],
    queryFn: async () => (await api.get<Duel[]>("/athlete/duels/my")).data,
    staleTime: 30_000,
  });
}

export function useDuelGuard() {
  return useQuery<GuardOut>({
    queryKey: ["athlete-duels", "precheck"],
    queryFn: async () =>
      (await api.get<GuardOut>("/athlete/duels/precheck")).data,
    staleTime: 60_000,
  });
}

function invalidateDuels(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ["athlete-duels"] });
}

export function useCreateDuel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateDuelIn): Promise<CreateDuelOut> => {
      const r = await api.post<CreateDuelOut>("/athlete/duels", payload);
      return r.data;
    },
    onSuccess: () => invalidateDuels(qc),
  });
}

export function useAcceptDuel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: { challengeId: number; bypassGuard?: boolean }) => {
      const r = await api.post(`/athlete/duels/${params.challengeId}/accept`, {
        bypass_guard: params.bypassGuard ?? false,
      });
      return r.data;
    },
    onSuccess: () => invalidateDuels(qc),
  });
}

export function useCancelDuel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (challengeId: number) => {
      await api.post(`/athlete/duels/${challengeId}/cancel`);
    },
    onSuccess: () => invalidateDuels(qc),
  });
}
